// ══════════════════════════════════════════════════════════════════════
// useScrollRestore
//
// React Router v6 ships <ScrollRestoration /> only with the data router
// (createBrowserRouter). HELIOS uses BrowserRouter + Routes, so we wire
// the same behavior by hand:
//
//   - Forward navigation (PUSH or REPLACE) → scroll to top.
//   - Back navigation (POP) → restore the scroll Y previously saved
//     for that path.
//
// Linear, Notion, Reeder all do this. Without it, going "back" from
// a long detail page lands at the top of the previous list, losing
// where the user was.
// ══════════════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const positions = new Map<string, number>();

export function useScrollRestore(): void {
  const location = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    // Save the scroll position of the previous path on every navigation,
    // before the new render happens. We use beforeunload-style cleanup
    // via a returned cleanup that runs when location changes.
    const key = location.key;
    return () => {
      positions.set(key, window.scrollY);
    };
  }, [location.key]);

  useEffect(() => {
    if (navType === 'POP') {
      // Back/forward — restore if we have a saved position, else top.
      const saved = positions.get(location.key) ?? 0;
      window.scrollTo(0, saved);
    } else {
      // PUSH/REPLACE — fresh navigation, scroll to top.
      window.scrollTo(0, 0);
    }
  }, [location.key, navType]);
}
